/**
 * Deleting the stored Meta token — the one destructive action on the WhatsApp
 * tab.
 *
 * Inline rather than in a dialog, the same way the SES card does it, and for the
 * same reason: what a confirmation has to say is not "are you sure" but **what is
 * lost**. Here that is the sealed token — Wend keeps no copy, and Meta will not
 * show a system user token twice either, so reconnecting almost always means
 * minting a new one. The confirmation's own button label says that, rather than
 * saying "Confirm".
 *
 * **Slice 16 moved it behind the Advanced disclosure and changed nothing about
 * it.** The reason to move it was weight: a full nested Card with a heading and
 * a description was the largest thing on a healthy connection, sitting directly
 * under the warning that says why messages stopped. Nothing about the two
 * presses, the wording, or what is deleted has been softened to suit the smaller
 * space — a confirmation trimmed to fit is a confirmation that has stopped doing
 * its job.
 *
 * It is its own file because `WhatsAppAdvanced` holds no request at all, and this
 * one does: importing the API client reaches `lib/config.ts` and `import.meta.env`,
 * which evaluates only inside Vite, so a disclosure that imported this could not
 * be rendered by `verify-whatsapp-tab.tsx`. The disclosure takes it as a slot.
 */
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  useToast,
} from '@wend/ui';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';

import { disconnectWhatsApp, type WhatsAppIntegration } from '../../lib/api/whatsapp';
import { RequestFailureAlert, requestFailure, type RequestFailure } from './request-failure';

export function WhatsAppDisconnect({
  integration,
  onDisconnected,
}: {
  integration: WhatsAppIntegration;
  onDisconnected: () => void;
}) {
  const { toast } = useToast();
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<RequestFailure | null>(null);

  async function onDisconnect(): Promise<void> {
    if (pending) return;
    setFailure(null);
    setPending(true);
    try {
      await disconnectWhatsApp();
      toast({
        tone: 'success',
        title: 'WhatsApp disconnected',
        description: 'The stored token was deleted.',
      });
      onDisconnected();
    } catch (error) {
      setFailure(requestFailure(error));
      setPending(false);
    }
  }

  return (
    <Card className="border-bad/25">
      <CardHeader>
        <CardTitle className="text-[1rem]">Disconnect WhatsApp</CardTitle>
        <CardDescription>
          This deletes the stored token rather than switching it off. Your templates stay — including
          the ones Meta approved, which live in your WABA — but nothing can be submitted or sent on
          WhatsApp until an account is connected again.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {failure !== null && <RequestFailureAlert failure={failure} />}

        {confirming ? (
          <div className="flex flex-col gap-3">
            <p className="max-w-prose text-[0.92rem] leading-relaxed text-text">
              Disconnect the account sending from{' '}
              <span className="font-mono text-[0.88rem]">
                {integration.displayPhoneNumber ?? integration.phoneNumberId}
              </span>
              ? The sealed token is deleted and Wend cannot recover it. Meta will not show that token
              again either, so reconnecting most likely means creating a new one.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={pending}
                className="border-bad/45 text-bad hover:border-bad/70 hover:bg-bad/10"
                onClick={() => {
                  void onDisconnect();
                }}
              >
                <Trash2 aria-hidden="true" className="size-4" />
                {pending ? 'Disconnecting' : 'Yes, disconnect and delete the token'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={pending}
                onClick={() => {
                  setConfirming(false);
                }}
              >
                Keep it connected
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setConfirming(true);
              }}
            >
              <Trash2 aria-hidden="true" className="size-4" />
              Disconnect
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
